import { changeURL } from '@/utils';
import debounce from 'lodash/debounce';

export function checkContainer(containerSelector: string): boolean {
  const container = document.querySelector(containerSelector);
  if (!container) {
    console.warn("容器不存在");
    return false; // 或者根据需要返回其他值
  }

  const containerHeight = container.clientHeight; // 可见高度
  const contentHeight = container.scrollHeight; // 内容总高度

  console.log(`容器高度: ${containerHeight}px`);
  console.log(`内容高度: ${contentHeight}px`);

  // 检测是否有滚动条
  return contentHeight > containerHeight; // 返回 true 或 false
}

/**
 * @description 判断元素是否在视口内
 */
function isElementInViewport(el: { getBoundingClientRect: () => any; }): boolean {
  const containerInfo = getContainerInfo();
  const containerElement = document.getElementById(containerInfo.id) as HTMLElement;
  if (!containerElement) return false;

  const containerBounds = containerElement.getBoundingClientRect();
  const targetBounds = el.getBoundingClientRect();

  // 计算元素在容器中的可见比例
  const elementTop = targetBounds.top;
  const elementBottom = targetBounds.bottom;
  const containerTop = containerBounds.top;
  const containerBottom = containerBounds.bottom;

  // 元素顶部在容器内或元素底部在容器内
  const targetTopIsIn = elementTop >= containerTop && elementTop <= containerBottom;
  const targetBottomIsIn = elementBottom >= containerTop && elementBottom <= containerBottom;

  return (targetTopIsIn || targetBottomIsIn);
}

export let current = 1;
// 记录上一次的滚动位置，用于判断滚动方向
let lastScrollTop = 0;

// 容器配置列表
let containerConfigs = [
  { id: 'MaterialList', selector: '#MaterialList .card-item__bg' },
  { id: 'favorite-list', selector: '#favorite-list .card-item__bg' },
  { id: 'brand-list', selector: '#brand-list .card-item__bg' },
  // 可以继续添加其他容器
];

/**
 * @description 添加新的容器配置
 */
export const addContainerConfig = (id: string, selector: string) => {
  // 检查是否已存在
  const existingIndex = containerConfigs.findIndex(config => config.id === id);
  if (existingIndex >= 0) {
    // 更新现有配置
    containerConfigs[existingIndex] = { id, selector };
  } else {
    // 添加新配置
    containerConfigs.push({ id, selector });
  }
};

/**
 * @description 获取当前页面的容器ID和选择器
 */
const getContainerInfo = () => {
  // 检查不同的容器是否存在，按优先级返回
  for (const container of containerConfigs) {
    const element = document.getElementById(container.id);
    if (element) {
      return container;
    }
  }

  // 默认返回第一个配置
  return containerConfigs[0];
};

/**
 * @description 专门处理向上滚动的页码计算
 */
export const calculatePageForUpScroll = (): number => {
  const containerInfo = getContainerInfo();
  const scrollItems = document.querySelectorAll(containerInfo.selector);

  if (scrollItems.length === 0) {
    return 1;
  }

  const containerElement = document.getElementById(containerInfo.id) as HTMLElement;
  if (!containerElement) return 1;

  const containerBounds = containerElement.getBoundingClientRect();
  const containerTop = containerBounds.top;
  const containerHeight = containerBounds.height;

  // 向上滚动时，重点关注容器顶部区域
  const topThreshold = containerHeight * 0.4; // 顶部40%区域
  const topViewportBottom = containerTop + topThreshold;

  let bestPage = 1;
  let maxTopVisibility = 0;

  scrollItems.forEach(function (item: any) {
    const itemBounds = item.getBoundingClientRect();
    const itemTop = itemBounds.top;
    const itemBottom = itemBounds.bottom;
    const itemHeight = itemBounds.height;

    // 计算元素在顶部视口区域的可见比例
    const visibleTop = Math.max(itemTop, containerTop);
    const visibleBottom = Math.min(itemBottom, topViewportBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const topVisibility = visibleHeight / itemHeight;

    if (topVisibility > maxTopVisibility) {
      maxTopVisibility = topVisibility;
      bestPage = Number(item?.dataset.page) || 1;
    }
  });

  // 调试信息
  if (process.env.NODE_ENV === 'development') {
    console.log('向上滚动页码计算:', {
      containerId: containerInfo.id,
      bestPage,
      maxTopVisibility,
      topThreshold,
      containerTop,
      topViewportBottom
    });
  }

  return bestPage;
};

/**
 * @description 专门处理向下滚动的页码计算
 */
export const calculatePageForDownScroll = (): number => {
  const containerInfo = getContainerInfo();
  const scrollItems = document.querySelectorAll(containerInfo.selector);

  if (scrollItems.length === 0) {
    return 1;
  }

  const containerElement = document.getElementById(containerInfo.id) as HTMLElement;
  if (!containerElement) return 1;

  const containerBounds = containerElement.getBoundingClientRect();
  const containerBottom = containerBounds.bottom;
  const containerHeight = containerBounds.height;

  // 向下滚动时，重点关注容器底部区域
  const bottomThreshold = containerHeight * 0.4; // 底部40%区域
  const bottomViewportTop = containerBottom - bottomThreshold;

  let bestPage = 1;
  let maxBottomVisibility = 0;

  scrollItems.forEach(function (item: any) {
    const itemBounds = item.getBoundingClientRect();
    const itemTop = itemBounds.top;
    const itemBottom = itemBounds.bottom;
    const itemHeight = itemBounds.height;

    // 计算元素在底部视口区域的可见比例
    const visibleTop = Math.max(itemTop, bottomViewportTop);
    const visibleBottom = Math.min(itemBottom, containerBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const bottomVisibility = visibleHeight / itemHeight;

    if (bottomVisibility > maxBottomVisibility) {
      maxBottomVisibility = bottomVisibility;
      bestPage = Number(item?.dataset.page) || 1;
    }
  });

  // 调试信息
  if (process.env.NODE_ENV === 'development') {
    console.log('向下滚动页码计算:', {
      containerId: containerInfo.id,
      bestPage,
      maxBottomVisibility,
      bottomThreshold,
      containerBottom,
      bottomViewportTop
    });
  }

  return bestPage;
};

/**
 * @description 计算当前页码 - 重写版本，解决向上滚动问题
 */
export const traversingElements = (isChange: boolean = true) => {
  const containerInfo = getContainerInfo();
  const scrollItems = document.querySelectorAll(containerInfo.selector);

  if (scrollItems.length === 0) {
    return 1;
  }

  const containerElement = document.getElementById(containerInfo.id) as HTMLElement;
  if (!containerElement) return 1;

  const currentScrollTop = containerElement.scrollTop;
  const isScrollingUp = currentScrollTop < lastScrollTop;
  lastScrollTop = currentScrollTop;

  // 根据滚动方向使用不同的计算策略
  let bestPage: number;

  if (isScrollingUp) {
    bestPage = calculatePageForUpScroll();
  } else {
    bestPage = calculatePageForDownScroll();
  }

  if (isChange) {
    changeURL('page', bestPage.toString());
  }

  return bestPage;
};

/**
 * @description 防抖的页码计算函数
 */
export const traversingElementsDebounced = debounce((): number => {
  const currentPage = traversingElements(false);
  return currentPage;
}, 200); // 进一步减少防抖时间，提高响应性

// 在开发环境中暴露容器管理功能
if (process.env.NODE_ENV === 'development') {
  (window as any).addContainerConfig = addContainerConfig;
  (window as any).getContainerConfigs = () => containerConfigs;
  (window as any).getCurrentContainerInfo = getContainerInfo;
}
